import { useCallback, useState } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import withFormStore, { useForm } from "../Form/withFormStore";
import Modal from "../Modals/Modal";
import EmailField from "../Form/Field/EmailField";
import Button from "../Buttons/Button";
import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";

import { sendConfirmationInstructions } from "../../mutations/SendConfirmationInstructionsMutation";
import { useFormatMessage } from "../Intl/Context";

const AuthenticationConfirmationForm = props => {
  const { onClose, email } = props;
  const { formState, validateForm } = useForm();

  const t = useFormatMessage();
  const labels = {
    title: t(intlMessages.title),
    description: t(intlMessages.description),
    requiredFieldsHint: t(formPageIntlMessages.requiredFieldsHint),
    email: t(intlMessages.email),
    submit: t(intlMessages.submit),
    cancel: t(intlMessages.cancel),
    successMessage: t(intlMessages.successMessage)
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formClassName = classNames({
    "authentication-fieldset": true,
    "authentication-sign-in": true,
    box: true,
    "margin-bottom-xs": true,
    "padding-x-lg": true,
    "padding-y-md": true,
    loading: isSubmitting
  });

  const breadcrumbs = [{ title: labels.title }];

  const handleSendResponse = useCallback(() => {
    setIsSubmitting(false);

    onClose(true, labels.successMessage);
  }, [labels.successMessage, onClose]);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      // Do not submit form if it is already submitting
      if (isSubmitting) return;

      if (validateForm()) {
        setIsSubmitting(true);

        sendConfirmationInstructions(formState, handleSendResponse);
      }
    },
    [formState, handleSendResponse, isSubmitting, validateForm]
  );

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      breadcrumbs={breadcrumbs}
      title={labels.title}
      className="modal-sm"
    >
      <form className={formClassName} onSubmit={handleSubmit}>
        <p className="help-block">{labels.description}</p>

        <p
          className="form-hint-required-fields margin-bottom-sm text-small"
          dangerouslySetInnerHTML={{ __html: labels.requiredFieldsHint }}
        />

        <EmailField
          fieldName="email"
          label={labels.email}
          required
          defaultValue={email}
          autoComplete="email"
        />

        <Button
          type="submit"
          className="btn btn-primary btn-block-md box-shadow-lg"
          onClick={handleSubmit}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          label={labels.submit}
          iconClassName="icon-arrow-right"
          iconPosition="after"
        />

        <Button
          type="button"
          className="btn btn-primary-inverse btn-block-md box-shadow-lg"
          onClick={onClose}
          label={labels.cancel}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          iconClassName="icon-arrow-right"
          iconPosition="after"
        />
      </form>
    </Modal>
  );
};

export default withFormStore(AuthenticationConfirmationForm);

const intlMessages = defineMessages({
  title: {
    id: "authentication.confirmationForm.title",
    defaultMessage: "Renvoi du mail de confirmation de compte"
  },
  description: {
    id: "authentication.confirmationForm.description",
    defaultMessage: `
      Un e-mail vous sera envoyé avec un lien vous permettant de confirmer
      votre compte. Vous serez alors automatiquement connecté.
    `
  },
  email: {
    id: "authentication.confirmationForm.labels.email",
    defaultMessage: "E-mail"
  },
  submit: {
    id: "authentication.confirmationForm.submit",
    defaultMessage: "Envoyer le mail"
  },
  cancel: {
    id: "authentication.passwordForm.cancel",
    defaultMessage: "Annuler"
  },
  successMessage: {
    id: "authentication.confirmationForm.successMessage",
    defaultMessage: `
      Si l'adresse e-mail renseignée est bien associée à un compte existant et que le 
      compte n'est pas encore confirmé, un e-mail a été envoyé avec les instructions de
      confirmation de votre compte.
    `
  }
});
